// @flow
import styles from "./AddOption.scss";

import moment from "moment";
import get from "lodash.get";
import React, { useState, useEffect, useRef } from "react";
import { type Match } from "react-router";

//import Material-ui components
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import FormHelperText from "@material-ui/core/FormHelperText";
import CircularProgress from "@material-ui/core/CircularProgress";

import AsyncFilteredSelect from "../../AsyncFilteredSelect";

import Options from "./Options";
import { type State as AddOptionState } from "../../../redux/modules/add-option.d";
import { type State as EventDetails } from "../../../redux/modules/event-details.d";
import isEqual from "lodash.isequal";
import SelectList from "../DefineEvents/SelectList/SelectList";

type SubmitFields = {
    seriesName: ?string,
    serieUuid: ?string,
    internal: string,
    promoterUuid: ?string,
    options: Option[],
    categoryUuid: ?string,
};

type Props = {
    id: string,
    match: Match,
    disabled?: boolean,
    translate: *,
    onClose: () => void,
    onSubmit: (fields: SubmitFields) => Promise<*>,
    /** Venue state */
    venues: *,
    loadVenues: () => *,
    /** Promoters */
    promoters: *,
    addPromoter: *,
    clearAddPromoter: *,
    loadOrganisers: *,
    /**event details */
    loadEventDetails: (uuid: string, isAddToSeriesEvent?: boolean) => void,
    clearEventDetails: *,
    eventDetails: EventDetails,
    /**events */
    series: *,
    findEvents: (filter: string) => *,
    /** state */
    state: {
        add: AddOptionState,
        create: *,
        addPromoter: *,
    },
    clearAddSerie: *,
    clearAddOption: *,
    refetchOrganisers: () => Promise<*>,
    categories: *,
    loadCategories: (uuid: string) => *,
};

type Option = {
    dateFrom: *,
    dateTo: *,
    venueUuid: string,
};

/**
 * Add dialog
 */
const AddOption = ({
    id,
    match,
    disabled: disabledProp,
    translate,
    onClose,
    onSubmit,
    venues,
    loadVenues,
    promoters,
    clearAddPromoter,
    loadOrganisers,
    loadEventDetails,
    clearEventDetails,
    eventDetails,
    series,
    findEvents,
    state,
    clearAddSerie,
    clearAddOption,
    categories,
    loadCategories,
}: Props) => {
    const [seriesName, setSeriesName] = useState(null);
    const [serieUuid, setSerieUuid] = useState(null);
    const [internal, setInternal] = useState("");
    const [promoterUuid, setPromoterUuid] = useState(null);
    const [options, setOptions] = useState([]);
    const [category, setCategory] = useState(null);
    const [showDateRangeConfirm, setShowDateRangeConfirm] = useState(false);

    const optionsEndRef = useRef(null);
    const prevEventDetailsDataRef = useRef(null);

    /**
     * Get Series uuid from route params
     */
    const uuid = (() => {
        const paramUuid = match.params.uuid;
        return paramUuid
            ? paramUuid === "none"
                ? null
                : paramUuid.toString()
            : null;
    })();

    /**
     * Get date from route params
     */
    const date = (() => {
        const paramDate = match.params.date;
        let d = paramDate ? paramDate.slice(0, -3).toString() : null;
        if (d) d = moment.utc(d);
        return d;
    })();

    /**
     * Get venue uuid from route params
     */
    const venueUuid = (() => {
        const paramDate = match.params.date;
        const venueCode = paramDate ? paramDate.slice(-2) : null;
        const venue =
            !venues.loading &&
            venues.data &&
            venueCode &&
            venues.data.find(venue => venue.code === venueCode);
        return venue ? venue.uuid : null;
    })();

    const eventDetailsData =
        !eventDetails.loading && eventDetails.data ? eventDetails.data : null;

    const disabled =
        disabledProp ||
        state.add.loading ||
        state.create.loading ||
        eventDetails.loading;

    const defaultPromoter =
        promoterUuid &&
        promoters.data &&
        promoters.data.find(p => p.uuid === promoterUuid);

    const parsedOptions = options.map(option => {
        const offsetFrom = moment(option.dateFrom).utcOffset();
        const offsetTo = moment(option.dateTo).utcOffset();
        return {
            ...option,
            dateFrom: moment
                .utc(option.dateFrom)
                .add(offsetFrom, "m")
                .toISOString(),
            dateTo: moment.utc(option.dateTo).add(offsetTo, "m").toISOString(),
        };
    });

    /**
     * Get errors
     */
    const getError = (key: string) => {
        const { add, create } = state;

        let transformedKey = key;

        if (serieUuid && add.fieldErrors) {
            if (key === "serie") return;
            if (key === "internal") return;
            return get(add.fieldErrors, transformedKey);
        }

        if (create.fieldErrors) {
            if (key === "serie") transformedKey = "optionCode";
            return (
                get(create.fieldErrors, transformedKey) ||
                get(create.fieldErrors, `createOptionRequest.${transformedKey}`)
            );
        }
        if (key === "formErrors" && (add.error || create.error)) {
            if (add.error) return add.error;
            if (create.error) return create.error;
        }
    };

    /**
     * Add option with same venue as previously selected options
     */
    const addOptionItem = () => {
        if (!venues.data || !venues.data.length) return;

        setOptions(prev => [
            ...prev,
            {
                dateFrom: date
                    ? date
                    : (prev.length && prev[prev.length - 1].dateFrom) ||
                      moment().startOf("day").add(1, "days"),
                dateTo: date
                    ? date
                    : (prev.length && prev[prev.length - 1].dateTo) ||
                      moment().startOf("day").add(1, "days"),
                venueUuid: venueUuid
                    ? venueUuid
                    : (prev.length && prev[prev.length - 1].venueUuid) ||
                      (venues.data.length && venues.data[0].uuid),
            },
        ]);
        setTimeout(() => optionsEndRef.current?.scrollIntoView(), 0);
    };

    /**
     * Clear states
     */
    const clear = () => {
        clearAddOption();
        clearAddSerie();
        clearAddPromoter();
        clearEventDetails();
    };

    const itemToLabel = (item: *) => {
        if (item) {
            const {
                uuid: itemUuid,
                name,
                internal: itemInternal,
                optionCode: itemOptCode,
                label,
            } = item;
            if (itemUuid) {
                const displayName =
                    name && itemOptCode
                        ? `${name} - ${itemOptCode}`
                        : `${name || itemOptCode}`;
                return (
                    displayName + (itemInternal ? ` ( ${itemInternal} )` : "")
                );
            }
            return label || name;
        }
        return "";
    };

    /**
     * onSubmit
     */
    const handleSubmit = (event: *) => {
        event.preventDefault();
        const hasLongRange = parsedOptions.some(
            option =>
                moment(option.dateTo).diff(moment(option.dateFrom), "days") >=
                20,
        );

        if (hasLongRange) {
            setShowDateRangeConfirm(true);
        }
        onSubmit({
            seriesName,
            serieUuid,
            internal,
            promoterUuid,
            options: parsedOptions,
            categoryUuid: category,
        }).then(() => onClose());
    };

    /**
     * On mount: clear, load venues/organisers, load event details if uuid present
     */
    useEffect(() => {
        clear();
        loadVenues();
        loadOrganisers();
        if (loadCategories) loadCategories(uuid);
        if (uuid) {
            loadEventDetails(uuid, true);
        }
    }, []);

    /**
     * Ensure at least one option is present when venues are loaded
     */
    useEffect(() => {
        if (!options.length && venues.data && venues.data.length) {
            addOptionItem();
        }
    }, [venues.data, options.length]);

    /**
     * Sync state from eventDetails when it changes
     */
    useEffect(() => {
        const data = eventDetails.data;
        if (data && !isEqual(prevEventDetailsDataRef.current, data)) {
            prevEventDetailsDataRef.current = data;
            setPromoterUuid(data.firstPromoterUuid);
            setSerieUuid(data.uuid);
            setInternal(data.internal || "");
            setCategory(data.categoryUuid);
        }
    }, [eventDetails.data]);

    return (
        <div>
            <Dialog maxWidth="md" className={styles.element} open>
                <form onSubmit={handleSubmit}>
                    <DialogTitle className={styles.title} disableTypography>
                        <Typography variant="h2" color="inherit">
                            {translate("Add Option")}
                        </Typography>
                        <Typography color="inherit" style={{ opacity: "0.75" }}>
                            {translate("Add an option to the calender")}
                        </Typography>
                    </DialogTitle>

                    {eventDetails.loading ? (
                        <DialogContent
                            style={{ textAlign: "center", width: "38em" }}
                        >
                            <CircularProgress className={styles.spinner} />
                        </DialogContent>
                    ) : (
                        <DialogContent
                            style={{
                                maxHeight: "40em",
                                overflowX: "scroll",
                            }}
                        >
                            <Grid container spacing={5}>
                                <Grid item xs={6}>
                                    <AsyncFilteredSelect
                                        id={`${id}-serie`}
                                        disabled={disabled}
                                        error={
                                            !!(
                                                getError("serie") ||
                                                series.error
                                            )
                                        }
                                        helperText={
                                            series.error || getError("serie")
                                        }
                                        label={translate("Series")}
                                        currentItem={
                                            (uuid && eventDetailsData) || ""
                                        }
                                        asyncState={series}
                                        lookup={findEvents}
                                        onSelect={serie => {
                                            if (!serie) return;
                                            if (serie.uuid) {
                                                setSerieUuid(serie.uuid);
                                                setSeriesName(serie.name);
                                                setInternal(serie.internal);
                                                setPromoterUuid(
                                                    serie.firstPromoterUuid,
                                                );
                                                setCategory(serie.categoryUuid);
                                            } else {
                                                setSerieUuid(null);
                                                setSeriesName(serie.name);
                                                setInternal("");
                                                setPromoterUuid(null);
                                                setCategory(null);
                                            }
                                        }}
                                        itemToString={item =>
                                            (item &&
                                                (item.name && item.optionCode
                                                    ? `${item.name} - ${item.optionCode}`
                                                    : item.name ||
                                                      item.optionCode)) ||
                                            ""
                                        }
                                        itemToLabel={itemToLabel}
                                        createNewItem={name =>
                                            name && {
                                                uuid: null,
                                                name,
                                                label: translate(
                                                    'Add "%{name}" as a new series',
                                                    { name },
                                                ),
                                            }
                                        }
                                        fullWidth
                                        autoFocus
                                        InputLabelProps={{
                                            shrink: true,
                                            classes: {
                                                root: styles.label,
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <AsyncFilteredSelect
                                        id={`${id}-promoter`}
                                        disabled={disabled}
                                        error={
                                            !!(
                                                state.addPromoter.error ||
                                                getError("promoterUuid")
                                            )
                                        }
                                        helperText={
                                            (state.addPromoter.error &&
                                                state.addPromoter.error
                                                    .error) ||
                                            (!state.addPromoter.error &&
                                                getError("promoterUuid"))
                                        }
                                        currentItem={defaultPromoter || ""}
                                        label={translate("Organiser")}
                                        asyncState={promoters}
                                        onSelect={promoter => {
                                            setPromoterUuid(
                                                promoter.uuid || null,
                                            );
                                        }}
                                        itemToString={item =>
                                            (item && item.name) || ""
                                        }
                                        itemToLabel={item =>
                                            (item &&
                                                (item.label || item.name)) ||
                                            ""
                                        }
                                        filter={(value, items) =>
                                            items
                                                .filter(item =>
                                                    item.name
                                                        .toLowerCase()
                                                        .includes(
                                                            value.toLowerCase(),
                                                        ),
                                                )
                                                .splice(0, 5)
                                        }
                                        fullWidth
                                        InputLabelProps={{
                                            classes: {
                                                root: styles.label,
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        id={`${id}-internal`}
                                        label={translate("Memo")}
                                        error={!!getError("internal")}
                                        helperText={getError("internal")}
                                        onChange={({ target: { value } }) =>
                                            setInternal(value)
                                        }
                                        value={internal}
                                        disabled={
                                            disabled ||
                                            !!serieUuid ||
                                            !seriesName
                                        }
                                        fullWidth
                                    />
                                </Grid>
                                <Grid
                                    color="inherit"
                                    style={{
                                        opacity: "0.5",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                    item
                                    xs={6}
                                    offset={6}
                                >
                                    <SelectList
                                        id="add-option-chooseCategory"
                                        onSelect={value => setCategory(value)}
                                        venueCode="SEA-BLUE"
                                        optionStatus={category}
                                        color="dark"
                                        categories={categories}
                                        type="CATEGORY"
                                        label={translate("Category")}
                                    />
                                </Grid>
                            </Grid>
                            <Options
                                id={`${id}-options`}
                                disabled={disabled}
                                options={options}
                                venues={venues}
                                translate={translate}
                                onChange={newOptions => setOptions(newOptions)}
                                getError={getError}
                            />
                            <div ref={optionsEndRef} />
                        </DialogContent>
                    )}

                    <DialogActions>
                        {getError("formErrors") && (
                            <FormHelperText id={`${id}-helper-text`} error>
                                {getError("formErrors")}
                            </FormHelperText>
                        )}
                        <Button
                            id={`${id}-add-date`}
                            disabled={disabled}
                            onClick={addOptionItem}
                            color="primary"
                        >
                            {translate("Add Date")}
                        </Button>
                        <Button
                            id={`${id}-cancel`}
                            onClick={() => {
                                onClose();
                                clear();
                            }}
                            disabled={disabled}
                        >
                            {translate("Cancel")}
                        </Button>
                        <Button
                            id={`${id}-submit`}
                            type="submit"
                            color="primary"
                            disabled={disabled}
                        >
                            {translate("Add Option")}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            <Dialog open={showDateRangeConfirm}>
                <DialogTitle>{translate("Confirm date range")}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {translate("Selected more than 20 days")}
                    </Typography>
                    {parsedOptions.length > 0 && (
                        <ul
                            style={{
                                paddingLeft: 16,
                            }}
                        >
                            {parsedOptions?.map(option => (
                                <li key={option.dateFrom}>
                                    <Typography>
                                        {`${moment(option.dateFrom).format(
                                            "DD/MM/YY",
                                        )}
                                     -
                                    ${moment(option.dateTo).format(
                                        "DD/MM/YY",
                                    )}`}
                                        <span
                                            style={
                                                moment(option.dateTo).diff(
                                                    moment(option.dateFrom),
                                                    "days",
                                                ) >= 20
                                                    ? {
                                                          color: "#f00",
                                                          marginLeft: 4,
                                                          fontWeight: "bold",
                                                      }
                                                    : {
                                                          marginLeft: 4,
                                                      }
                                            }
                                        >
                                            (
                                            {moment(option.dateTo).diff(
                                                moment(option.dateFrom),
                                                "days",
                                            ) + 1}
                                            {translate("day")})
                                        </span>
                                    </Typography>
                                </li>
                            ))}
                        </ul>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowDateRangeConfirm(false)}>
                        {translate("Cancel")}
                    </Button>
                    <Button
                        color="primary"
                        onClick={() => {
                            setShowDateRangeConfirm(false);
                            onSubmit({
                                seriesName,
                                serieUuid,
                                internal,
                                promoterUuid,
                                options: parsedOptions,
                                categoryUuid: category,
                            }).then(() => onClose());
                        }}
                    >
                        {translate("Confirm")}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default AddOption;
